import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, Link } from 'react-router-dom';
import './App.css';
import { LoginForm } from './components/LoginForm';
import { LoginCallback } from './components/LoginCallback';
import { ForgotPasswordPage } from './components/ForgotPasswordPage';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import Header from './components/Header';
import CrossAssetDashboard from './components/dashboards/CrossAssetDashboard';
import { isDashboardRoute } from './config/navigation';
import { capturePageView, initAnalytics, syncAnalyticsCaptureForRoute } from './lib/analytics';
import { trackPageVisitEvent } from './lib/authTelemetry';
import { emitAppResumeRefresh } from './lib/appResumeRefresh';
import { getCanonicalRedirectUrl } from './lib/canonicalOrigin';
import { isDailyPulseEnabled } from './lib/dailyPulseAvailability';
import { canAccessThemesFeature, canAccessWeekendMoversFeature } from './lib/featureFlags';
import RouteLoadingSkeleton, { RouteLoadingVariant } from './components/ui/RouteLoadingSkeleton';

const AssetDetailsPage = lazy(() => import('./components/AssetDetails'));
const NotFoundPage = lazy(() => import('./components/NotFound'));
const DailyPulseListPage = lazy(() => import('./components/DailyPulseList'));
const NewsletterListPage = lazy(() => import('./components/NewsletterList'));
const NewsletterListEditorialExperimentPage = lazy(() => import('./components/NewsletterListEditorialExperiment'));
const MarketsListPage = lazy(() => import('./components/MarketsList'));
const AdminDashboardPage = lazy(() => import('./components/AdminDashboard'));
const AssetClassDerbyPage = lazy(() => import('./components/AssetClassDerby'));
const StrategiesPage = lazy(() => import('./components/StrategiesPage'));
const StrategyDetailPage = lazy(() => import('./components/StrategyDetailPage'));
const MarketContextPage = lazy(() => import('./components/market-context/MarketContextPage'));
const FedWatchPage = lazy(() => import('./components/fed-watch/FedWatchPage'));
const TradeLogPage = lazy(() => import('./components/trade-log/TradeLogPage'));
const ThemesPage = lazy(() => import('./components/themes/ThemesPage'));
const ThemesWeeklyBriefPage = lazy(() => import('./components/themes/ThemesWeeklyBriefPage'));
const ThemesWatchlistPage = lazy(() => import('./components/themes/ThemesWatchlistPage'));
const StockDetailPage = lazy(() => import('./components/themes/StockDetailPage'));
const ExpectedReturnPage = lazy(() => import('./components/themes/ExpectedReturnPage'));
const WeekendMoversPage = lazy(() => import('./components/weekend-movers/WeekendMoversPage'));
const CpoTickerInfoPage = lazy(() => import('./components/CpoTickerInfoPage'));
const MacroDashboardPage = lazy(() => import('./components/dashboards/MacroDashboard'));
const BondsDashboardPage = lazy(() => import('./components/dashboards/BondsDashboard'));
const CommoditiesDashboardPage = lazy(() => import('./components/dashboards/CommoditiesDashboard'));
const CurrenciesDashboardPage = lazy(() => import('./components/dashboards/CurrenciesDashboard'));
const CryptoDashboardPage = lazy(() => import('./components/dashboards/CryptoDashboard'));
const EquitiesGlobalDashboardPage = lazy(() => import('./components/dashboards/EquitiesGlobalDashboard'));
const EquitiesThematicDashboardPage = lazy(() => import('./components/dashboards/EquitiesThematicDashboard'));
const EquitiesUsDashboardPage = lazy(() => import('./components/dashboards/EquitiesUsDashboard'));
const HistoricalAllocationDashboardPage = lazy(() => import('./components/dashboards/HistoricalAllocationDashboard'));
const EtfComponentsDashboardPage = lazy(() => import('./components/dashboards/EtfComponentsDashboard'));
const FactorsDashboardMockupPage = lazy(() => import('./components/dashboards/FactorsDashboardMockup'));
const ExecutionEditMockupPage = lazy(() => import('./components/playbook/ExecutionEditMockupPage'));
type ThemeMode = 'light' | 'dark';
const THEME_STORAGE_KEY = 'qcl-theme';
const BUILD_MANIFEST_PATH = '/asset-manifest.json';
const STANDALONE_RESUME_REFRESH_MIN_HIDDEN_MS = 1000;
const STANDALONE_RESUME_REFRESH_COOLDOWN_MS = 5000;
const STANDALONE_RESUME_FOCUS_IGNORE_MS = 1500;
const APP_BUNDLE_CHECK_COOLDOWN_MS = 30000;
const APP_BUNDLE_CHECK_MIN_HIDDEN_MS = 1000;
const APP_BUNDLE_CHECK_FOCUS_IGNORE_MS = 1500;
const SNAPSHOT_RESTORE_RELOAD_COOLDOWN_MS = 5000;
const SNAPSHOT_RESTORE_RELOAD_SESSION_KEY = 'qcl:snapshot-restore-reload';

const isStandaloneAppSession = (): boolean => {
  if (typeof window === 'undefined') return false;

  const navigatorStandalone =
    'standalone' in window.navigator &&
    Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
  const displayModeStandalone =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;

  return navigatorStandalone || displayModeStandalone;
};

const isMobileWebKitSession = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent || '';
  const isAppleWebKit = /AppleWebKit/i.test(userAgent);
  const isiOSDevice = /iPhone|iPad|iPod/i.test(userAgent);
  const isiPadDesktopClass =
    /Macintosh/i.test(userAgent) &&
    typeof window.navigator.maxTouchPoints === 'number' &&
    window.navigator.maxTouchPoints > 1;

  return isAppleWebKit && (isiOSDevice || isiPadDesktopClass);
};

const isSnapshotBackedRoute = (pathname: string): boolean => {
  if (
    pathname === '/' ||
    pathname === '/strategies' ||
    pathname === '/us-equities' ||
    pathname === '/us-equities/watchlist' ||
    pathname === '/us-equities/weekly-brief'
  ) {
    return true;
  }

  if (pathname.startsWith('/strategies/')) {
    return true;
  }

  return [
    '/dashboards/macro',
    '/dashboards/bonds',
    '/dashboards/allocation-history',
    '/dashboards/commodities',
    '/dashboards/currencies',
    '/dashboards/crypto',
    '/dashboards/equities/global',
    '/dashboards/equities/thematic',
    '/dashboards/equities/us',
  ].includes(pathname);
};

const getInitialTheme = (): ThemeMode => {
  if (typeof window === 'undefined') return 'light';
  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (storedTheme === 'light' || storedTheme === 'dark') {
    return storedTheme;
  }
  return 'light';
};

const normalizeBundlePath = (value: string | null | undefined): string | null => {
  if (!value) return null;

  try {
    return new URL(value, window.location.origin).pathname;
  } catch {
    return null;
  }
};

const getCurrentMainBundlePath = (): string | null => {
  if (typeof document === 'undefined') return null;

  for (const script of Array.from(document.scripts)) {
    const src = script.getAttribute('src');
    if (!src || !src.includes('/static/js/main.') || !src.endsWith('.js')) continue;
    const normalized = normalizeBundlePath(src);
    if (normalized) return normalized;
  }

  return null;
};

const DashboardLoginGate: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { userEmail } = useAuth();
  const location = useLocation();

  if (userEmail) {
    return children;
  }

  const pricingUrl =
    'https://www.quantchecklabs.com/pricing?utm_source=platform&utm_medium=dashboard_gate&utm_campaign=anon_to_paid';
  const loginPath = `/login?redirect=${encodeURIComponent(
    `${location.pathname}${location.search || ''}`
  )}`;

  return (
    <div className="mx-auto max-w-[920px] px-4 py-[14px] md:px-7 md:py-5">
      <section className="rounded-[14px] border border-[var(--qcl-border)] bg-white p-6 shadow-[var(--qcl-shadow)]">
        <h2 className="text-[20px] font-bold tracking-[-0.02em] text-[var(--qcl-text)]">You're not signed in</h2>
        <p className="mt-2 text-[14px] text-[var(--qcl-text-dim)]">
          Live dashboard data requires a Premium Subscription. Log in or sign up with a free account to view delayed data.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <a
            href={pricingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-[44px] items-center rounded-[10px] bg-[var(--qcl-accent)] px-4 py-2 text-[13px] font-semibold text-white transition hover:bg-[var(--qcl-accent-hover)]"
          >
            See pricing for live data
          </a>
          <Link
            to={loginPath}
            className="inline-flex min-h-[44px] items-center rounded-[10px] border border-[var(--qcl-border)] px-4 py-2 text-[13px] font-semibold text-[var(--qcl-text)] transition hover:bg-[var(--qcl-bg)]"
          >
            Log in or sign up for delayed data
          </Link>
        </div>
      </section>
    </div>
  );
};

const ThemesFeatureGate: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { userEmail, accessToken, authReady } = useAuth();
  const canOptimisticallyEnterThemes = Boolean(userEmail && canAccessThemesFeature(userEmail) && accessToken);

  if (!authReady && !canOptimisticallyEnterThemes) {
    return <RouteLoadingSkeleton variant="content" />;
  }

  if (!userEmail || !canAccessThemesFeature(userEmail)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const WeekendMoversFeatureGate: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { userEmail, authReady } = useAuth();

  if (!authReady) {
    return <RouteLoadingSkeleton variant="content" />;
  }

  if (!userEmail || !canAccessWeekendMoversFeature(userEmail)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const { userEmail, accessToken, isPremium } = useAuth();
  const [theme, setTheme] = useState<ThemeMode>(getInitialTheme);
  const hiddenStartedAtRef = useRef<number | null>(null);
  const lastResumeRefreshAtRef = useRef(0);
  const bundleCheckHiddenStartedAtRef = useRef<number | null>(null);
  const lastBundleCheckAtRef = useRef(0);
  const bundleCheckInFlightRef = useRef(false);
  const canonicalRedirectUrl =
    typeof window !== 'undefined' ? getCanonicalRedirectUrl(window.location) : null;

  const isAuthPage =
    location.pathname === '/login' ||
    location.pathname === '/login-callback' ||
    location.pathname === '/forgot-password';
  const isLoginPage = location.pathname === '/login';
  const isAdminRoute = location.pathname === '/admin';
  const onDashboardRoute = isDashboardRoute(location.pathname);
  const dailyPulseEnabled = isDailyPulseEnabled();

  const withFallback = (element: React.ReactElement, variant: RouteLoadingVariant = 'content') => (
    <Suspense fallback={<RouteLoadingSkeleton variant={variant} />}>{element}</Suspense>
  );

  useEffect(() => {
    if (!canonicalRedirectUrl) return;
    window.location.replace(canonicalRedirectUrl);
  }, [canonicalRedirectUrl]);

  useEffect(() => {
    if (canonicalRedirectUrl) return;
    initAnalytics();
  }, [canonicalRedirectUrl]);

  useEffect(() => {
    if (canonicalRedirectUrl) return;
    syncAnalyticsCaptureForRoute(location.pathname);
    capturePageView(location.pathname, location.search, {
      userStatus: userEmail ? (isPremium ? 'premium' : 'free') : 'anonymous',
      isPremium: Boolean(userEmail && isPremium),
      isAuthenticated: Boolean(userEmail),
    });
    trackPageVisitEvent({
      email: userEmail,
      accessToken,
      isPremium: Boolean(userEmail && isPremium),
      source: 'react_router',
      pagePath: `${location.pathname}${location.search || ''}`,
    });
  }, [canonicalRedirectUrl, location.pathname, location.search, userEmail, accessToken, isPremium]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle('dark', theme === 'dark');
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
  }, [theme]);

  useEffect(() => {
    if (canonicalRedirectUrl || typeof document === 'undefined' || typeof window === 'undefined') return;

    const effectStartedAt = Date.now();

    const checkForUpdatedBundle = async () => {
      if (bundleCheckInFlightRef.current) return;

      const now = Date.now();
      if (now - lastBundleCheckAtRef.current < APP_BUNDLE_CHECK_COOLDOWN_MS) {
        return;
      }

      const currentBundlePath = getCurrentMainBundlePath();
      if (!currentBundlePath) return;

      lastBundleCheckAtRef.current = now;
      bundleCheckInFlightRef.current = true;

      try {
        const manifestResponse = await fetch(
          `${BUILD_MANIFEST_PATH}?_ts=${encodeURIComponent(String(now))}`,
          {
            cache: 'no-store',
            credentials: 'same-origin',
          }
        );
        if (!manifestResponse.ok) {
          return;
        }

        const manifest = (await manifestResponse.json()) as {
          files?: Record<string, string>;
          entrypoints?: string[];
        };
        const manifestMainPath =
          normalizeBundlePath(manifest?.files?.['main.js']) ||
          normalizeBundlePath(
            Array.isArray(manifest?.entrypoints)
              ? manifest.entrypoints.find((entry) => typeof entry === 'string' && entry.includes('/static/js/main.')) ||
                  null
              : null
          );

        if (manifestMainPath && manifestMainPath !== currentBundlePath) {
          window.location.reload();
        }
      } catch (bundleCheckError) {
        console.warn('Failed to check for a newer frontend bundle.', bundleCheckError);
      } finally {
        bundleCheckInFlightRef.current = false;
      }
    };

    void checkForUpdatedBundle();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        bundleCheckHiddenStartedAtRef.current = Date.now();
        return;
      }

      if (document.visibilityState !== 'visible') {
        return;
      }

      const hiddenStartedAt = bundleCheckHiddenStartedAtRef.current;
      bundleCheckHiddenStartedAtRef.current = null;
      if (hiddenStartedAt === null) {
        return;
      }

      if (Date.now() - hiddenStartedAt >= APP_BUNDLE_CHECK_MIN_HIDDEN_MS) {
        void checkForUpdatedBundle();
      }
    };

    const handlePageShow = () => {
      if (Date.now() - effectStartedAt < APP_BUNDLE_CHECK_FOCUS_IGNORE_MS) return;
      void checkForUpdatedBundle();
    };

    const handleWindowFocus = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - effectStartedAt < APP_BUNDLE_CHECK_FOCUS_IGNORE_MS) return;
      void checkForUpdatedBundle();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [canonicalRedirectUrl]);

  useEffect(() => {
    if (canonicalRedirectUrl || typeof document === 'undefined' || typeof window === 'undefined') return;
    if (!isStandaloneAppSession()) return;

    const effectStartedAt = Date.now();

    const refreshForResume = () => {
      const now = Date.now();
      if (now - lastResumeRefreshAtRef.current < STANDALONE_RESUME_REFRESH_COOLDOWN_MS) {
        return;
      }

      lastResumeRefreshAtRef.current = now;
      emitAppResumeRefresh();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenStartedAtRef.current = Date.now();
        return;
      }

      if (document.visibilityState !== 'visible') {
        return;
      }

      const hiddenStartedAt = hiddenStartedAtRef.current;
      hiddenStartedAtRef.current = null;
      if (hiddenStartedAt === null) {
        return;
      }

      if (Date.now() - hiddenStartedAt >= STANDALONE_RESUME_REFRESH_MIN_HIDDEN_MS) {
        refreshForResume();
      }
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        refreshForResume();
      }
    };

    const handleWindowFocus = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - effectStartedAt < STANDALONE_RESUME_FOCUS_IGNORE_MS) return;
      refreshForResume();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [canonicalRedirectUrl]);

  useEffect(() => {
    if (canonicalRedirectUrl || typeof window === 'undefined') return;
    if (isStandaloneAppSession()) return;
    if (!isMobileWebKitSession()) return;
    if (!isSnapshotBackedRoute(location.pathname)) return;

    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;

      const routeKey = `${location.pathname}${location.search || ''}`;
      const storageKey = `${SNAPSHOT_RESTORE_RELOAD_SESSION_KEY}:${routeKey}`;
      const now = Date.now();

      try {
        const previousValue = window.sessionStorage.getItem(storageKey);
        const previousReloadAt = previousValue ? Number(previousValue) : 0;
        if (Number.isFinite(previousReloadAt) && now - previousReloadAt < SNAPSHOT_RESTORE_RELOAD_COOLDOWN_MS) {
          return;
        }
        window.sessionStorage.setItem(storageKey, String(now));
      } catch {
        // Ignore sessionStorage failures and fall back to a one-time reload attempt.
      }

      window.location.reload();
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [canonicalRedirectUrl, location.pathname, location.search]);

  if (canonicalRedirectUrl) {
    return null;
  }

  return (
    <div className="flex min-h-screen min-w-0 flex-col">
      {!isAdminRoute && (
        <Header
          showLoginButton={!isLoginPage}
          hideNavigation={isAuthPage}
          theme={theme}
          onToggleTheme={() => setTheme((current) => (current === 'dark' ? 'light' : 'dark'))}
        />
      )}
      <main
        className={`min-w-0 flex-1 overflow-x-hidden ${
          isAdminRoute ? '' : isAuthPage ? 'pt-16' : onDashboardRoute ? 'pt-24 md:pt-16' : 'pt-16'
        } pb-24 md:pb-6 ${isAuthPage || isAdminRoute ? '' : 'md:ml-[232px]'}`}
      >
        <Routes>
          <Route path="/login-callback" element={<LoginCallback />} />
          <Route path="/login" element={<LoginForm />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/asset/:ticker" element={withFallback(<AssetDetailsPage />)} />
          <Route
            path="/dashboards/commodities/cpo1"
            element={withFallback(<CpoTickerInfoPage />, 'dashboard')}
          />
          <Route
            path="/etf/:ticker"
            element={withFallback(
              <DashboardLoginGate>
                <EtfComponentsDashboardPage />
              </DashboardLoginGate>,
              'dashboard'
            )}
          />

          {dailyPulseEnabled && (
            <>
              <Route path="/dailypulseold" element={withFallback(<DailyPulseListPage />)} />
              <Route path="/pulse" element={withFallback(<NewsletterListPage />)} />
              <Route
                path="/pulse-editorial-experiment"
                element={withFallback(<NewsletterListEditorialExperimentPage />)}
              />
              <Route path="/dailypulse" element={<Navigate to="/pulse" replace />} />
              <Route path="/newsletter" element={<Navigate to="/pulse" replace />} />
            </>
          )}

          <Route path="/rankings" element={withFallback(<AssetClassDerbyPage />)} />
          <Route
            path="/weekend-movers"
            element={
              <WeekendMoversFeatureGate>
                {withFallback(<WeekendMoversPage />)}
              </WeekendMoversFeatureGate>
            }
          />
          <Route
            path="/us-equities/weekly-brief"
            element={
              <ThemesFeatureGate>
                {withFallback(<ThemesWeeklyBriefPage />)}
              </ThemesFeatureGate>
            }
          />
          <Route
            path="/us-equities/watchlist"
            element={
              <ThemesFeatureGate>
                {withFallback(<ThemesWatchlistPage />)}
              </ThemesFeatureGate>
            }
          />
          <Route
            path="/us-equities"
            element={
              <ThemesFeatureGate>
                {withFallback(<ThemesPage />)}
              </ThemesFeatureGate>
            }
          />
          <Route
            path="/us-equities/:ticker"
            element={
              <ThemesFeatureGate>
                {withFallback(<StockDetailPage />)}
              </ThemesFeatureGate>
            }
          />
          <Route
            path="/expected-return"
            element={
              <ThemesFeatureGate>
                {withFallback(<ExpectedReturnPage />)}
              </ThemesFeatureGate>
            }
          />
          <Route path="/themes" element={<Navigate to="/us-equities" replace />} />
          <Route path="/strategies" element={withFallback(<StrategiesPage />)} />
          <Route path="/strategies/:slug" element={withFallback(<StrategyDetailPage />)} />
          <Route path="/market-context" element={withFallback(<MarketContextPage />)} />
          <Route path="/fed-watch" element={withFallback(<FedWatchPage />)} />
          <Route path="/trade-log" element={withFallback(<TradeLogPage />)} />
          <Route path="/derby" element={<Navigate to="/rankings" replace />} />

          <Route
            path="/dashboards/macro"
            element={withFallback(
              <DashboardLoginGate>
                <MacroDashboardPage />
              </DashboardLoginGate>,
              'dashboard'
            )}
          />
          <Route
            path="/dashboards/bonds"
            element={withFallback(
              <DashboardLoginGate>
                <BondsDashboardPage />
              </DashboardLoginGate>,
              'dashboard'
            )}
          />
          <Route
            path="/dashboards/allocation-history"
            element={withFallback(
              <DashboardLoginGate>
                <HistoricalAllocationDashboardPage />
              </DashboardLoginGate>,
              'dashboard'
            )}
          />
          <Route
            path="/dashboards/commodities"
            element={withFallback(
              <DashboardLoginGate>
                <CommoditiesDashboardPage />
              </DashboardLoginGate>,
              'dashboard'
            )}
          />
          <Route
            path="/dashboards/currencies"
            element={withFallback(
              <DashboardLoginGate>
                <CurrenciesDashboardPage />
              </DashboardLoginGate>,
              'dashboard'
            )}
          />
          <Route
            path="/dashboards/crypto"
            element={withFallback(
              <DashboardLoginGate>
                <CryptoDashboardPage />
              </DashboardLoginGate>,
              'dashboard'
            )}
          />
          <Route
            path="/dashboards/equities/global"
            element={withFallback(
              <DashboardLoginGate>
                <EquitiesGlobalDashboardPage />
              </DashboardLoginGate>,
              'dashboard'
            )}
          />
          <Route
            path="/dashboards/equities/thematic"
            element={withFallback(
              <DashboardLoginGate>
                <EquitiesThematicDashboardPage />
              </DashboardLoginGate>,
              'dashboard'
            )}
          />
          <Route
            path="/dashboards/equities/us"
            element={withFallback(
              <DashboardLoginGate>
                <EquitiesUsDashboardPage />
              </DashboardLoginGate>,
              'dashboard'
            )}
          />
          <Route
            path="/dashboards/factors-mockup"
            element={withFallback(<FactorsDashboardMockupPage />, 'dashboard')}
          />
          <Route
            path="/mockups/execution-edit"
            element={withFallback(<ExecutionEditMockupPage />)}
          />

          <Route path="/polymarket" element={withFallback(<MarketsListPage />)} />
          <Route path="/admin" element={withFallback(<AdminDashboardPage />, 'dashboard')} />
          <Route path="/not-found" element={withFallback(<NotFoundPage />)} />

          <Route path="/" element={<CrossAssetDashboard />} />
          <Route path="*" element={<Navigate to="/not-found" replace />} />
        </Routes>
      </main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
};

export default App; 
